// Recruiting pipeline (mirror of backend recruit_pipeline.py): 10 stages, labels, colors and helpers.
export const PIPELINE_STAGES = [
  { key: "identification", label: "Identification", short: "ID" },
  { key: "evaluation", label: "Evaluation", short: "EVAL" },
  { key: "coordinator_review", label: "Coordinator Review", short: "COORD" },
  { key: "gm_review", label: "GM Review", short: "GM" },
  { key: "exec_review", label: "Executive Director Review", short: "EXEC" },
  { key: "hc_approval", label: "Head Coach Approval", short: "HC" },
  { key: "offer_extended", label: "Offer Extended", short: "OFFER" },
  { key: "commitment", label: "Commitment", short: "COMMIT" },
  { key: "signed", label: "Signed", short: "SIGNED" },
  { key: "enrolled", label: "Enrolled", short: "ENROLLED" },
];
export const STAGE_KEYS = PIPELINE_STAGES.map((s) => s.key);
const BY_KEY = Object.fromEntries(PIPELINE_STAGES.map((s) => [s.key, s]));
export const stageIndex = (k) => Math.max(0, STAGE_KEYS.indexOf(k));
export const stageLabel = (k) => BY_KEY[k]?.label || "Identification";
export const stageShort = (k) => BY_KEY[k]?.short || "ID";
export const stageProgress = (k) => Math.round((100 * (stageIndex(k) + 1)) / STAGE_KEYS.length);
export const isClosed = (r) => !!r?.pipeline?.closed;
export const atLeast = (r, k) => stageIndex(r?.stage) >= stageIndex(k);
// Marked committed by the GM / HC / Chief of Staff while the approval chain keeps running.
export const isKnownCommit = (r) => !!r?.pipeline?.known_commit && !atLeast(r, "commitment");
export const isOffered = (r) => atLeast(r, "offer_extended") && !atLeast(r, "commitment") && !isKnownCommit(r);
export const isCommitted = (r) => atLeast(r, "commitment") || isKnownCommit(r);
export const isSigned = (r) => atLeast(r, "signed");
// Incoming class: committed (pipeline or marked) or signed, not yet on the roster, not closed.
export const isIncoming = (r) => !isClosed(r) && (["commitment", "signed"].includes(r?.stage) || isKnownCommit(r));
export const PIPELINE_WAITING_EVENT = "omni-pipeline-waiting";
export const recruitStageText = (r) => (isClosed(r) ? `Closed · ${stageLabel(r?.stage)}` : stageLabel(r?.stage));

// Stage tint along the funnel: slate → amber (approvals) → green (offer onward).
export const stageColor = (k) => {
  const i = stageIndex(k);
  if (i === 0) return "#94a3b8";
  if (i === 1) return "#38bdf8";
  if (i <= 5) return "#f59e0b";
  if (i <= 7) return "#22c55e";
  return "#14b8a6";
};

export const STEP_STATUS_META = {
  done: { label: "Approved", color: "#22c55e" },
  pre_pipeline: { label: "Pre-pipeline", color: "#64748b" },
  skipped: { label: "Skipped", color: "#a78bfa" },
  not_required: { label: "Not required", color: "#52525b" },
  pending: { label: "Pending", color: "#f59e0b" },
};
export const fmtWhen = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleString([], { month: "short", day: "numeric", hour: iso.length === 10 ? undefined : "numeric", minute: iso.length === 10 ? undefined : "2-digit" }) : "");
export const fmtHours = (h) => (h == null ? "—" : h < 1 ? "<1h" : h < 48 ? `${Math.round(h)}h` : `${Math.round(h / 24)}d`);

export const EVENT_LABEL = {
  identify: "Identified", advance: "Stage advanced", approve: "Approved", early_review: "Early review", early_counted: "Early review counted",
  skip: "Skipped", send_back: "Sent back", close: "Closed", reopen: "Reopened", edit: "Edited review", offer: "Authorized offer extended",
  commit: "Commitment", sign: "Signed", enroll: "Enrolled", assign: "Staff assigned", milestone: "Milestone", not_required: "Not required",
  evaluation: "Evaluation logged", evaluation_edit: "Evaluation edited", evaluation_deleted: "Evaluation deleted",
  known_commit: "Marked committed", clear_commit: "Commitment removed",
};
export const STEP_LABEL = {
  identification: "Identified", evaluation: "Position Coach evaluation", coord_side: "OC / DC review", coord_rc: "Recruiting Coordinator review",
  gm: "GM review", exec: "Executive Director review", hc: "Head Coach approval", offer: "Authorized offer", commitment: "Commitment", signed: "Signed", enrolled: "Enrolled",
};

export const RECRUITING_EXEC_ROLES = ["super_admin", "athletic_director", "deputy_ad", "chief_of_staff", "gm", "head_coach"];
